import { DefaultApi } from "@coeff/api";

import { config } from "../config/index";
import { sentryCapture } from "../utils/sentry";

const allowedChatWindowOrigin = new URL(config.BASE_URL).origin;

export type ZendeskClient = (
  arg1: string,
  arg2: string,
  arg3?: Record<string, string> | ((any: any) => void)
) => void;

const getJWTToken = async (apiClient: DefaultApi) => {
  try {
    const { data: response } = await apiClient.getZendeskChatSessionJWT();

    return response.chat_session_jwt;
  } catch (error) {
    console.error(error);

    sentryCapture({
      error,
      name: "Zendesk JWT Fetch: POST /api/zendesk_chat_session_jwt",
    });

    return;
  }
};

let isChatOpen = false;

// Central listeners for "chat opened" — invoked whenever the widget opens from any entry point.
const onChatOpenListeners = new Set<() => void>();

export const registerOnZendeskChatOpen = (listener: () => void) => {
  onChatOpenListeners.add(listener);
  return () => {
    onChatOpenListeners.delete(listener);
  };
};

const notifyChatOpened = () => {
  onChatOpenListeners.forEach(listener => listener());
};

// Loading state store for useSyncExternalStore
let isChatLoading = false;
const chatLoadingListeners = new Set<() => void>();

const setChatLoading = (loading: boolean) => {
  isChatLoading = loading;
  chatLoadingListeners.forEach(listener => listener());
};

export const subscribeZendeskChatLoading = (listener: () => void) => {
  chatLoadingListeners.add(listener);
  return () => {
    chatLoadingListeners.delete(listener);
  };
};

export const getZendeskChatLoadingSnapshot = () => isChatLoading;

export const dismissZendeskChatLoading = () => {
  setChatLoading(false);
};

// --- New-window mode state ---
let chatWindowRef: Window | null = null;
let chatWindowClosePoller: ReturnType<typeof setInterval> | null = null;
let chatWindowMessageHandler: ((event: MessageEvent) => void) | null = null;

const cleanupChatWindow = () => {
  if (chatWindowClosePoller) {
    clearInterval(chatWindowClosePoller);
    chatWindowClosePoller = null;
  }
  if (chatWindowMessageHandler) {
    window.removeEventListener("message", chatWindowMessageHandler);
    chatWindowMessageHandler = null;
  }
  chatWindowRef = null;
  isChatOpen = false;
};

/** Ping the chat window via postMessage; resolves true if it responds within timeout. */
const pingChatWindow = (timeoutMs = 500): Promise<boolean> => {
  if (!chatWindowRef || chatWindowRef.closed) {
    return Promise.resolve(false);
  }
  return new Promise(resolve => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== allowedChatWindowOrigin) {
        return;
      }
      try {
        const msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (msg.type === "zendesk:pong") {
          window.removeEventListener("message", handler);
          resolve(true);
        }
      } catch {
        // ignore non-JSON messages
      }
    };
    window.addEventListener("message", handler);
    chatWindowRef!.postMessage(JSON.stringify({ type: "zendesk:ping" }), allowedChatWindowOrigin);
    setTimeout(() => {
      window.removeEventListener("message", handler);
      resolve(false);
    }, timeoutMs);
  });
};

const openZendeskChatInNewWindow = async (
  apiClient: DefaultApi,
  options: {
    zendeskMessengerEnabled: boolean;
    user?: { name?: string; email: string };
    onUnreadMessages?: (count: number) => void;
  }
): Promise<void> => {
  // If we think the window is open, verify it's actually alive via ping/pong.
  // This guards against stale isChatOpen state (e.g. browser crash, tab discard).
  if (isChatOpen) {
    const alive = await pingChatWindow();
    if (alive) {
      window.open("", "coeff-zendesk-chat");
      return;
    }
    // Window is gone — reset state and open a fresh one.
    cleanupChatWindow();
  }

  const params = new URLSearchParams();
  params.set("messenger_enabled", String(options.zendeskMessengerEnabled));
  params.set("opener_origin", window.location.origin);

  chatWindowRef = window.open(
    `/zendesk-chat?${params.toString()}`,
    "coeff-zendesk-chat",
    "width=420,height=750,menubar=no,toolbar=no,status=no"
  );

  if (!chatWindowRef) {
    console.error("[Zendesk] Failed to open chat window (popup blocked?)");
    return;
  }

  isChatOpen = true;

  // Set up postMessage listener for messages from the chat window
  if (chatWindowMessageHandler) {
    window.removeEventListener("message", chatWindowMessageHandler);
  }
  chatWindowMessageHandler = (event: MessageEvent) => {
    if (event.origin !== allowedChatWindowOrigin) {
      return;
    }
    let msg: { type: string; count?: number; token?: string };
    try {
      msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }
    if (!msg || typeof msg.type !== "string") {
      return;
    }

    switch (msg.type) {
      case "zendesk:jwtRequest":
        // Chat window is requesting a Zendesk JWT — fetch it and respond
        getJWTToken(apiClient)
          .then(token => {
            if (token && chatWindowRef && !chatWindowRef.closed) {
              chatWindowRef.postMessage(
                JSON.stringify({ type: "zendesk:jwt", token }),
                allowedChatWindowOrigin
              );
            }
          })
          .catch(error => console.error("[Zendesk] JWT fetch for chat window failed:", error));
        break;
      case "zendesk:open":
        notifyChatOpened();
        break;
      case "zendesk:unreadMessages":
        if (options.onUnreadMessages) {
          options.onUnreadMessages(msg.count!);
        }
        break;
      case "zendesk:closed":
        cleanupChatWindow();
        break;
    }
  };
  window.addEventListener("message", chatWindowMessageHandler);

  // Poll as backup close detection (in case beforeunload doesn't fire)
  chatWindowClosePoller = setInterval(() => {
    if (chatWindowRef && chatWindowRef.closed) {
      cleanupChatWindow();
    }
  }, 2000);

  notifyChatOpened();
};

export const openZendeskChat = (
  apiClient: DefaultApi,
  options: {
    zendeskMessengerEnabled: boolean;
    user?: {
      name?: string;
      email: string;
    };
    onUnreadMessages?: (count: number) => void;
  }
) => {
  return openZendeskChatInNewWindow(apiClient, options);
};
